/**
 * Tiny pub/sub store. Pairs with React 18's useSyncExternalStore for
 * lock-step rendering across components without prop drilling.
 *
 * Usage:
 *   const counter = createStore(0);
 *   counter.subscribe(() => console.log(counter.get()));
 *   counter.set(1);
 *
 * In React:
 *   const value = useSyncExternalStore(counter.subscribe, counter.get);
 */

type Listener = () => void;

export type Store<T> = {
  get: () => T;
  set: (next: T | ((prev: T) => T)) => void;
  subscribe: (l: Listener) => () => void;
};

export function createStore<T>(initial: T): Store<T> {
  let state = initial;
  const listeners = new Set<Listener>();
  return {
    get: () => state,
    set: (next) => {
      const resolved =
        typeof next === 'function' ? (next as (p: T) => T)(state) : next;
      if (Object.is(resolved, state)) return;
      state = resolved;
      listeners.forEach((l) => l());
    },
    subscribe: (l) => {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
  };
}
